import { ref } from 'vue'
import { cookie } from '@/utils/cookie'
import { setError } from '@/utils/errorHandler' // Import the setError function

/**
 * Composable to manage event details.
 * @param {string} slug - The slug identifier for the event.
 * @returns {object} - Reactive references and methods related to event details.
 */
export function eventDetails (slug) {
  // Reactive references to store event data
  const event = ref({})
  const attendee = ref({})
  const isSignedUp = ref(false)
  const attendeeId = ref(false)
  const isPayable = ref(false)
  const eventId = ref(false)
  const isPaid = ref(false)
  const vAFh = ref(false)
  const eventState = ref(false)

  /**
   * Fetches event details from the API.
   */
  async function fetchEventDetails () {
    try {
      const response = await fetch(
        `${window.vueAppConfig.endpoint}/api/v2/events/fetch`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            slug: slug,
            brand_id: window.vueAppConfig.info.id,
            session_id: cookie()
          })
        }
      )

      // Handle specific HTTP error status codes
      if ([429, 403, 500, 404].includes(response.status)) {
        let message = ''
        let waitTime = null

        switch (response.status) {
          case 429:
            message =
              'You have made too many requests. Please wait before trying again.'
            waitTime = 60 // Set desired wait time
            break
          case 403:
            message = 'You do not have permission to access this resource.'
            break
          case 500:
            message =
              'An unexpected server error occurred. Please try again later.'
            break
          case 404:
            message = 'The requested event was not found.'
            break
          default:
            message = 'An unexpected error occurred.'
        }

        // Set the error using the singleton error handler
        setError(response.status, message, waitTime)
        return // Exit the function to prevent further processing
      }

      const {
        status,
        event: eventData,
        attendee: attendeeData
      } = await response.json()

      if (status && eventData) {
        // Populate the reactive references with fetched data
        eventId.value = eventData.id
        event.value = eventData

        if (eventData.price) {
          isPayable.value = true
        }

        eventState.value = eventData.status

        if (attendeeData) {
          vAFh.value = attendeeData.vafh
          attendeeId.value = attendeeData.user.id
          isSignedUp.value = true
          attendee.value = attendeeData

          if (attendeeData.payment.paid) {
            isPaid.value = true
            isPayable.value = false
          }
        }
      } else {
        console.error(
          'Failed to load event details: Invalid response structure.'
        )

        // Trigger a generic error if the response is not as expected
        setError(404, 'The requested event was not found.')
      }
    } catch (error) {
      console.error('Error fetching event details:', error)

      // Handle network or unexpected errors
      setError(500, 'A network error occurred while fetching event details.')
    }
  }

  /**
   * Marks the user as signed up for the event.
   */
  function signup () {
    isSignedUp.value = true
    // Add additional signup logic here if necessary
  }

  return {
    event,
    attendee,
    isSignedUp,
    attendeeId,
    eventId,
    isPaid,
    eventState,
    fetchEventDetails,
    vAFh,
    signup,
    isPayable
  }
}
